import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Problem from './components/Problem';
import Solution from './components/Solution';
import ServiceFlow from './components/ServiceFlow';
import Authority from './components/Authority';
import FAQ from './components/FAQ';
import ServiceArea from './components/ServiceArea';
import Contact from './components/Contact';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    // Add Schema.org JSON-LD for LocalBusiness
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "株式会社ライズコーポレーション",
      "image": "https://static.readdy.ai/image/a44b33c2a19c260b8697c4ae9afddc14/df05b769b1b7d05ef32882c327169def.png",
      "description": "大阪市天王寺区の太陽光蓄電池専門会社。太陽光発電システム・蓄電池の設置、メンテナンス、補助金申請サポートまで一貫対応。",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "上汐3-8-24 ライズ上汐ビル8階",
        "addressLocality": "天王寺区",
        "addressRegion": "大阪市",
        "postalCode": "543-0002",
        "addressCountry": "JP"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "34.6565",
        "longitude": "135.5144"
      },
      "telephone": "0120-710-056",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      "areaServed": [
        {
          "@type": "State",
          "name": "大阪府"
        },
        {
          "@type": "State",
          "name": "兵庫県"
        },
        {
          "@type": "State",
          "name": "京都府"
        },
        {
          "@type": "State",
          "name": "奈良県"
        },
        {
          "@type": "State",
          "name": "滋賀県"
        },
        {
          "@type": "State",
          "name": "和歌山県"
        }
      ],
      "priceRange": "$$",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com"
    };

    // Add Schema.org JSON-LD for FAQPage
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "太陽光発電システムの設置に専門知識は必要ですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "いいえ、専門知識は一切不要です。当社の経験豊富なスタッフが、お客様のご要望をヒアリングし、最適な太陽光発電・蓄電池システムをご提案いたします。建物の構造や屋根の状態を考慮した安全な施工計画を立案し、すべての手続きを代行いたします。"
          }
        },
        {
          "@type": "Question",
          "name": "補助金や助成金の申請サポートはありますか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "はい、各自治体の太陽光発電・蓄電池に関する補助金・助成金の申請を全面的にサポートいたします。申請書類の作成から提出まで、専門スタッフが丁寧にサポートいたしますので、お気軽にご相談ください。"
          }
        },
        {
          "@type": "Question",
          "name": "設置後のメンテナンスや保証も対応していますか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "はい、設置工事完了後の定期メンテナンス、システムの点検、万が一のトラブル対応まで一貫してサポートいたします。また、長期保証制度もご用意しておりますので、安心してご利用いただけます。"
          }
        },
        {
          "@type": "Question",
          "name": "対応エリアはどこまでですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "大阪府、兵庫県、京都府、奈良県、滋賀県、和歌山県を中心に対応しております。その他のエリアについてもご相談可能ですので、まずはお気軽にお問い合わせください。"
          }
        },
        {
          "@type": "Question",
          "name": "見積もりは無料ですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "はい、お見積もりは完全無料です。現地調査を行い、屋根の状態や日照条件を確認した上で、詳細なお見積もりを作成いたします。お見積もり後のキャンセルも可能ですので、お気軽にご依頼ください。"
          }
        },
        {
          "@type": "Question",
          "name": "どのようなメーカーの製品に対応していますか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "国内外の主要メーカーの太陽光パネル・蓄電池に対応しております。お客様のご予算やご要望に合わせて、最適なメーカー・製品をご提案いたします。各メーカーの特徴や性能についても詳しくご説明いたします。"
          }
        }
      ]
    };

    // Add WebPage schema
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "太陽光蓄電池案件丸投げ代行/太陽光のことならライズコーポレーション",
      "description": "大阪市天王寺区の太陽光蓄電池専門会社ライズコーポレーション。太陽光発電システム・蓄電池の設置、メンテナンス、補助金申請サポートまで一貫対応。",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "inLanguage": "ja",
      "isPartOf": {
        "@type": "WebSite",
        "name": "株式会社ライズコーポレーション",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com"
      }
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(localBusinessSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(faqSchema);
    document.head.appendChild(script2);

    const script3 = document.createElement('script');
    script3.type = 'application/ld+json';
    script3.text = JSON.stringify(webPageSchema);
    document.head.appendChild(script3);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
      document.head.removeChild(script3);
    };
  }, []);

  return (
    <div className="min-h-screen bg-teal-50/30">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center">
              <h1>
                <img 
                  src="https://static.readdy.ai/image/a44b33c2a19c260b8697c4ae9afddc14/df05b769b1b7d05ef32882c327169def.png" 
                  alt="ライズコーポレーション" 
                  className="h-9 md:h-12 w-auto object-contain"
                />
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-1">
              <button onClick={() => scrollToSection('problem')} className="text-gray-600 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer font-medium px-3 py-2 rounded-lg hover:bg-teal-50 text-sm">お困りごと</button>
              <button onClick={() => scrollToSection('solution')} className="text-gray-600 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer font-medium px-3 py-2 rounded-lg hover:bg-teal-50 text-sm">5大メリット</button>
              <button onClick={() => scrollToSection('service-flow')} className="text-gray-600 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer font-medium px-3 py-2 rounded-lg hover:bg-teal-50 text-sm">サービスの流れ</button>
              <button onClick={() => scrollToSection('authority')} className="text-gray-600 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer font-medium px-3 py-2 rounded-lg hover:bg-teal-50 text-sm">実績・信頼</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-600 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer font-medium px-3 py-2 rounded-lg hover:bg-teal-50 text-sm">よくある質問</button>
              <button onClick={() => scrollToSection('service-area')} className="text-gray-600 hover:text-teal-500 transition-colors whitespace-nowrap cursor-pointer font-medium px-3 py-2 rounded-lg hover:bg-teal-50 text-sm">対応エリア</button>
              <div className="ml-3">
                <button onClick={() => scrollToSection('contact')} className="bg-gradient-to-r from-teal-400 to-emerald-500 text-white px-5 py-2.5 rounded-full hover:from-teal-500 hover:to-emerald-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer font-semibold text-sm">
                  無料相談・お見積り
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 cursor-pointer"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 w-full bg-gray-600 transition-all ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block h-0.5 w-full bg-gray-600 transition-all ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block h-0.5 w-full bg-gray-600 transition-all ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </div>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-3 border-t border-gray-100">
              <button onClick={() => scrollToSection('problem')} className="block w-full text-left py-2.5 text-gray-600 hover:text-teal-500 hover:bg-teal-50 px-4 cursor-pointer rounded-xl text-sm">お困りごと</button>
              <button onClick={() => scrollToSection('solution')} className="block w-full text-left py-2.5 text-gray-600 hover:text-teal-500 hover:bg-teal-50 px-4 cursor-pointer rounded-xl text-sm">5大メリット</button>
              <button onClick={() => scrollToSection('service-flow')} className="block w-full text-left py-2.5 text-gray-600 hover:text-teal-500 hover:bg-teal-50 px-4 cursor-pointer rounded-xl text-sm">サービスの流れ</button>
              <button onClick={() => scrollToSection('authority')} className="block w-full text-left py-2.5 text-gray-600 hover:text-teal-500 hover:bg-teal-50 px-4 cursor-pointer rounded-xl text-sm">実績・信頼</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left py-2.5 text-gray-600 hover:text-teal-500 hover:bg-teal-50 px-4 cursor-pointer rounded-xl text-sm">よくある質問</button>
              <button onClick={() => scrollToSection('service-area')} className="block w-full text-left py-2.5 text-gray-600 hover:text-teal-500 hover:bg-teal-50 px-4 cursor-pointer rounded-xl text-sm">対応エリア</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2.5 bg-gradient-to-r from-teal-400 to-emerald-500 text-white hover:from-teal-500 hover:to-emerald-600 px-4 mt-2 rounded-xl cursor-pointer font-semibold text-sm">無料相談・お見積り</button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-16 md:pt-24">
        <Hero onCTAClick={() => scrollToSection('contact')} />
        <Problem />
        <Solution />
        <ServiceFlow />
        <Authority />
        <FAQ />
        <ServiceArea />
        <Contact />
      </main>
    </div>
  );
}